import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useDispatch, useSelector } from 'react-redux';

import MainWrapper from '../components/Wrappers/MainWrapper';
import { usePastCreation } from '../services/pastCreationService';
import searchIcon from '../assets/images/unionSearch.svg';
import smartSearchIcon from '../assets/images/Search.svg';
import { setPageNumber, setPreviousPage } from '../redux/pastCreateionsSlice';
import LineLoader from '../components/Loaders/LineLoader';
import ImageModal from '../components/Modals/ImageModal';
import NoPastCreationFound from '../components/Modals/NoPastCreationFound';
import { CopyIconSvg } from '../components/ui/Svgs';
import spinner from '../assets/images/spinner.png';
import ReactGA from "react-ga4";
import TipsForBestResults from '../components/ui/TipsForBestResults';

const PastCreation = () => {
  const dispatch = useDispatch();
  const pastCreation = usePastCreation();

  const { isPastCreationLoading, pastCreationData, page, previousPage, totalPages, } = useSelector((state) => state.pastCreation);

  const [searchPrompt, setSearchPrompt] = useState('');
  const [expandedStates, setExpandedStates] = useState({});
  const [copiedStates, setCopiedStates] = useState({});
   const [imageModalWidth, setImageModalWidth] = useState(0);
  const [checkPastCreationData, setCheckPastCreationData] = useState(!pastCreationData.length ? true : false);

  const observer = useRef();
  const abortController = useRef();


  // Debounce function to delay search input
  const debounce = (func, delay) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  };

  // Debounced search input handler
  const handleSearch = debounce((value) => {
    dispatch(setPreviousPage(0))
    dispatch(setPageNumber(1)); // Reset to page 1 on new search
    setSearchPrompt(value);
  }, 500);

  const fetchPastCreations = useCallback(() => {
    if (abortController.current) abortController.current.abort();
    abortController.current = new AbortController();

    if (previousPage === page) return;

    pastCreation(page, searchPrompt, abortController.current.signal);
  }, [page, searchPrompt, checkPastCreationData]);

  useEffect(() => {
    fetchPastCreations()

  }, [fetchPastCreations]);
  
  useEffect(() => {
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  // Observe last item for infinite scrolling
  const lastItemRef = useCallback(
    (node) => {
      if (isPastCreationLoading) return;
      if (page === totalPages) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          dispatch(setPageNumber(page + 1));
        }
      });

      if (node) observer.current.observe(node);
    },
    [isPastCreationLoading, page, dispatch]
  );

  // Toggle expanded state for a specific prompt
  const toggleExpand = (index) => {
    setExpandedStates((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const handleCopy = (index, text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopiedStates({
          [index]: true,
        });

        setTimeout(() => {
          setCopiedStates((prevState) => ({
            ...prevState,
            [index]: false,
          }));
        }, 2000);
      })
      .catch((err) => console.error('Failed to copy text:', err));
  };

  return (
    <MainWrapper>
      <div className='flex flex-col h-full overflow-hidden'>
        {/* Header */}
        <div className='h-[136px] px-[16px] py-[40px] flexBetween border-b'>
          <h1 className='text-[32px] lg:text-[40px] font-helveticaDisp-Bold text_cus_primary leading-[48px] tracking-[-0.02em] font-[700] '>Your past creations</h1>
          <div className='h-[58px] w-[448px] px-[24px] py-[16px] bg-[#F3F4F6] flex items-center gap-[8px] border rounded-[16px]'>
            <img src={searchIcon} alt='search icon' className='h-[16px]' />
            <input
              type='text'
              placeholder='Search by prompt'
              className='w-full outline-none bg-transparent placeholder:tracking-[-0.02em] text-gray-primary'
              onChange={(e) => handleSearch(e.target.value)}
              onClick={
                () => {
                  ReactGA.gtag('event', 'search', {
                    action: 'Search clicked on past creations page',
                    label: 'Search clicked on past creations page'
                });
                }
              }
            />
          </div>
        </div>

        {/* Loading */}
        {isPastCreationLoading && page === 1 ? (
          <LineLoader />
        ) : (
          !pastCreationData?.length && !isPastCreationLoading) ?
          (<NoPastCreationFound />) :
          (<div className='overflow-y-scroll mr-[4px]'>
            {pastCreationData?.map((item, index) => {
              const isLastItem = index === pastCreationData.length - 1;
              const isExpanded = expandedStates[index] || false;
              const isCopied = copiedStates[index] || false;

              const displayText = isExpanded ? `${item.prompt}` : `${item.prompt.slice(0, 200)}`;

              return (
                <div
                  key={index}
                  ref={isLastItem ? lastItemRef : null}
                  className='observer-class px-[16px] py-[24px] flex flex-col gap-[24px] border-b'
                >
                  <div className=' flex justify-between items-start gap-[16px]'>
                    <div className='flex items-start gap-[16px]'>
                      <img src={smartSearchIcon} alt='' />
                      {/* <p>{item.prompt}</p> */}
                      <p className="pr-[20px] text-cus_primary leading-[24px] tracking-[-0.02em] text-justify  ">
                        {displayText}
                        {item.prompt.length > 200 && (
                          <span
                            onClick={() => toggleExpand(index)}
                            className="text-blue-primary cursor-pointer"
                          >
                            {isExpanded ? 'Show Less' : '...Know More'}
                          </span>
                        )}
                      </p>
                    </div>
                    {/* <img src={searchIcon} alt='' className='h-[16px]' /> */}
                    <div
                      onClick={() => handleCopy(index, item.prompt)}
                      className="flex items-center gap-[8px] cursor-pointer"
                    >
                      <CopyIconSvg fill={`${isCopied ? '#1A73E8' : '#1E242C'}`} />
                      <span className={`${isCopied ? 'text-blue-primary' : ''}`}>{isCopied ? 'Copied' : 'Copy'}</span>
                    </div>
                  </div>
                  <div className='grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3'>
                    {item?.images.map((image, i) => (
                      <ImageModal
                        key={i}
                        imageData={image}
                        editImagePositionRef
                        setImageModalWidth={setImageModalWidth}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            <TipsForBestResults />

            {/* Loader for Infinite Scroll */}
            {isPastCreationLoading && page > 1 && <div className='w-full h-[00px] py-[24px] flexCenter gap-[16px]'>
              <img src={spinner} alt="loading" className='animate-spin' />
            </div>}
          </div>)
        }
      </div>
    </MainWrapper>

  )
}

export default PastCreation

